// The route table: 50 lazy page imports across sixteen surfaces, and the four
// layouts that wrap them.
//
// This is the top of the stack and the biggest single act of composition in the
// app, which is why it lives in the composition root rather than in a layer of
// its own. Nothing below `app/` may import it — enforced by the
// `from: "./src/app", except: ["./bootstrap"]` clauses in `eslint.config.mjs`,
// the same clauses that let every layer read `app/bootstrap` and nothing else
// up here.
//
// URL *builders* are a different thing and live in `utils/paths.ts`: a string
// like `projectSetup(id)` is read by 68 files across every layer, so it sits
// beside the stack rather than on top of it. Both files used to be called
// "routes", which made "move routes up a layer" an ambiguous instruction.
import { BasicLayout } from "@layout/BasicLayout";
import { NoMenuLayout } from "@layout/NoMenuLayout";
import OrganisationLayout from "@layout/OrganisationLayout/OrganisationLayout";
import ProjectDashboardLayout from "@layout/ProjectDashboardLayout/ProjectDashboardLayout";
import * as RouteHelper from "@utils/paths";
import { lazy } from "react";
import { ProfileLineItem } from "catchment-common/interfaces/profile";

import { IS_PROD_ENV, IS_TEST_ENV } from "@app/bootstrap/init";

// Lazy load all page components
// Handle both default and named exports
const Login = lazy(() => import("@surfaces/auth/pages/Login"));
const Register = lazy(() => import("@surfaces/auth/pages/Register"));
const ForgotPassword = lazy(
  () => import("@surfaces/auth/pages/ForgotPassword")
);
const ApiDocs = lazy(() =>
  import("@surfaces/docs/pages/ApiDocs").then((module) => ({
    default: module.ApiDocs
  }))
);
const McpDocs = lazy(() =>
  import("@surfaces/docs/pages/McpDocs/McpDocs").then((module) => ({
    default: module.McpDocs
  }))
);
const ClaudeDocs = lazy(() =>
  import("@surfaces/docs/pages/ClaudeDocs/ClaudeDocs").then((module) => ({
    default: module.ClaudeDocs
  }))
);
const CreateAccount = lazy(() =>
  import("@surfaces/account/pages/CreateAccount").then((module) => ({
    default: module.CreateAccount
  }))
);
const EditAccount = lazy(() =>
  import("@surfaces/account/pages/EditAccount").then((module) => ({
    default: module.EditAccount
  }))
);
const OrganisationDashboard = lazy(() =>
  import("@surfaces/organisation/pages/OrganisationDashboard/OrganisationDashboard").then(
    (module) => ({
      default: module.OrganisationDashboard
    })
  )
);
const CreateProject = lazy(() =>
  import("@surfaces/project-setup/pages/CreateProject/CreateProject").then(
    (module) => ({
      default: module.CreateProject
    })
  )
);
const CreateEmployee = lazy(() =>
  import("@surfaces/workforce/pages/CreateEmployee").then((module) => ({
    default: module.CreateEmployee
  }))
);
const EmployeeSelfRegister = lazy(() =>
  import("@surfaces/workforce/pages/EmployeeSelfRegister").then((module) => ({
    default: module.EmployeeSelfRegister
  }))
);
const Projects = lazy(() =>
  import("@surfaces/organisation/pages/ListProjects").then((module) => ({
    default: module.ListProjects
  }))
);
const LastProjectRedirect = lazy(() =>
  import("@surfaces/organisation/pages/LastProjectRedirect").then((module) => ({
    default: module.LastProjectRedirect
  }))
);
const CatchmentAdmin = lazy(() =>
  import("@surfaces/organisation/pages/CatchmentAdmin").then((module) => ({
    default: module.CatchmentAdmin
  }))
);

const Employees = lazy(() =>
  import("@surfaces/workforce/pages/Employees").then((module) => ({
    default: module.Employees
  }))
);
const LegacyExportLibraryRedirect = lazy(() =>
  import("@surfaces/export-library/pages/LegacyExportLibraryRedirect").then(
    (module) => ({
      default: module.LegacyExportLibraryRedirect
    })
  )
);
const ExportGenerator = lazy(() =>
  import("@surfaces/export-library/pages/ExportGenerator/ExportGenerator").then(
    (module) => ({
      default: module.ExportGenerator
    })
  )
);
const OrganisationExportGenerator = lazy(() =>
  import("@surfaces/export-library/pages/ExportGenerator/OrganisationExportGenerator").then(
    (module) => ({
      default: module.OrganisationExportGenerator
    })
  )
);
const ListSROI = lazy(() =>
  import("@surfaces/sroi/pages/ListSROI").then((module) => ({
    default: module.ListSROI
  }))
);
const SROIBuilder = lazy(() =>
  import("@surfaces/sroi/pages/SROIBuilder").then((module) => ({
    default: module.SROIBuilder
  }))
);
const SROIReport = lazy(() =>
  import("@surfaces/sroi/pages/SROIReport").then((module) => ({
    default: module.SROIReport
  }))
);
const OrganisationDocumentsLevel1 = lazy(() =>
  import("@surfaces/organisation/pages/OrganisationDocuments/OrganisationDocumentsLevel1").then(
    (module) => ({
      default: module.OrganisationDocumentsLevel1
    })
  )
);
const FrameworkLibrary = lazy(() =>
  import("@surfaces/organisation/pages/FrameworkLibrary/FrameworkLibrary").then(
    (module) => ({
      default: module.FrameworkLibrary
    })
  )
);
const FrameworkVersions = lazy(() =>
  import("@surfaces/organisation/pages/FrameworkLibrary/FrameworkVersions").then(
    (module) => ({
      default: module.FrameworkVersions
    })
  )
);
const OrganisationCompliance = lazy(() =>
  import("@surfaces/analytics/pages/OrgCompliance/OrganisationCompliance").then(
    (module) => ({
      default: module.OrganisationCompliance
    })
  )
);
const CreateFrameworkLibrary = lazy(() =>
  import("@surfaces/organisation/pages/FrameworkLibrary/CreateFrameworkLibrary").then(
    (module) => ({
      default: module.CreateFrameworkLibrary
    })
  )
);
const OrganisationDocumentsLevel2 = lazy(() =>
  import("@surfaces/organisation/pages/OrganisationDocuments/OrganisationDocumentsLevel2").then(
    (module) => ({
      default: module.OrganisationDocumentsLevel2
    })
  )
);
const OrgLabourHoursDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgLabourHoursDashboard")
);
const OrgSocialSpendDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgSocialSpendDashboard")
);
const OrgEmissionsDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgEmissionsDashboard")
);
const OrgEnergyDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgEnergyDashboard")
);
const OrgWaterDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgWaterDashboard")
);
const OrgWasteDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgWasteDashboard")
);
const OrgMaterialsDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgMaterialsDashboard")
);
const OrgTransportDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgTransportDashboard")
);
const OrgLocalContentDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgLocalContentDashboard")
);
const OrgDocumentsDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgDocumentsDashboard")
);
const OrgFormsDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgFormsDashboard")
);
const OrgRegistersDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgRegistersDashboard")
);
const OrgSurveysDashboard = lazy(
  () => import("@surfaces/analytics/pages/OrgInsights/OrgSurveysDashboard")
);
const ForecastProjectLevel1 = lazy(() =>
  import("@surfaces/forecasting/pages/Forecast/ForecastProjectLevel1").then(
    (module) => ({
      default: module.ForecastProjectLevel1
    })
  )
);
const Forecast = lazy(() =>
  import("@surfaces/forecasting/pages/Forecast/ForecastLevel1").then(
    (module) => ({
      default: module.ForecastLevel1
    })
  )
);
const Dashboard = lazy(() =>
  import("@surfaces/dashboard/pages/Dashboard").then((module) => ({
    default: module.Dashboard
  }))
);
const ContractorPackages = lazy(() =>
  import("@surfaces/packages/pages/ListPackages").then((module) => ({
    default: module.ListPackages
  }))
);
const PackageDashboard = lazy(() =>
  import("@surfaces/packages/pages/PackageDashboard/PackageDashboard").then(
    (module) => ({
      default: module.PackageDashboard
    })
  )
);
const ProjectReports = lazy(() =>
  import("@surfaces/reports/pages/ProjectReports").then((module) => ({
    default: module.ProjectReports
  }))
);
const ProjectDetails = lazy(() =>
  import("@surfaces/dashboard/pages/Details/ProjectDetails").then((module) => ({
    default: module.ProjectDetails
  }))
);
const PackageReports = lazy(() =>
  import("@surfaces/reports/pages/PackageReports").then((module) => ({
    default: module.PackageReports
  }))
);
const AnalyticsDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/LabourHoursDashboard").then(
    (module) => ({
      default: module.LabourHoursDashboard
    })
  )
);
const SocialSpendDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/SocialSpendDashboard").then(
    (module) => ({
      default: module.SocialSpendDashboard
    })
  )
);
const LocalContentDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/LocalContentDashboard").then(
    (module) => ({
      default: module.LocalContentDashboard
    })
  )
);
const ComplianceDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/ComplianceDashboard").then(
    (module) => ({
      default: module.ComplianceDashboard
    })
  )
);
const EmissionsDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/EmissionsDashboard").then(
    (module) => ({
      default: module.EmissionsDashboard
    })
  )
);
const EnergyDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/EnergyDashboard").then(
    (module) => ({
      default: module.EnergyDashboard
    })
  )
);
const WaterDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/WaterDashboard").then(
    (module) => ({
      default: module.WaterDashboard
    })
  )
);
const WasteDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/WasteDashboard").then(
    (module) => ({
      default: module.WasteDashboard
    })
  )
);
const MaterialsDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/MaterialsDashboard").then(
    (module) => ({
      default: module.MaterialsDashboard
    })
  )
);
const TransportDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/TransportDashboard").then(
    (module) => ({
      default: module.TransportDashboard
    })
  )
);
const AnalyticsDocumentLevel1 = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/levels/AnalyticsDocumentLevel1").then(
    (module) => ({
      default: module.AnalyticsDocumentLevel1
    })
  )
);
const AnalyticsDocumentLevel2 = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/levels/AnalyticsDocumentLevel2").then(
    (module) => ({
      default: module.AnalyticsDocumentLevel2
    })
  )
);
const AnalyticsDocumentLevel3 = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/levels/AnalyticsDocumentLevel3").then(
    (module) => ({
      default: module.AnalyticsDocumentLevel3
    })
  )
);
const GovAssureDocumentsDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/GovAssureDocumentsDashboard").then(
    (module) => ({
      default: module.GovAssureDocumentsDashboard
    })
  )
);
const GovAssureFormsDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/GovAssureFormsDashboard").then(
    (module) => ({
      default: module.GovAssureFormsDashboard
    })
  )
);
const GovAssureSurveysDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/GovAssureSurveysDashboard").then(
    (module) => ({
      default: module.GovAssureSurveysDashboard
    })
  )
);
const GovAssureRegistersDashboard = lazy(() =>
  import("@surfaces/analytics/pages/AnalyticsDashboard/GovAssureRegistersDashboard").then(
    (module) => ({
      default: module.GovAssureRegistersDashboard
    })
  )
);
const ReadOnlyReportSubmission = lazy(() =>
  import("@surfaces/reports/pages/ReadOnlyReportSubmission/ReadOnlyReportSubmission").then(
    (module) => ({
      default: module.ReadOnlyReportSubmission
    })
  )
);
const ReportSubmission = lazy(() =>
  import("@surfaces/reports/pages/ReportSubmission").then((module) => ({
    default: module.ReportSubmission
  }))
);
const ReviewReportSection = lazy(() =>
  import("@surfaces/reports/pages/ReviewReportSection/ReviewReportSection").then(
    (module) => ({
      default: module.ReviewReportSection
    })
  )
);
const SmartDirectoryCompanies = lazy(() =>
  import("@surfaces/smart-directory/pages/SmartDirectory").then((module) => ({
    default: module.SmartDirectory
  }))
);
const ProjectEmployees = lazy(() =>
  import("@surfaces/workforce/pages/ProjectEmployees").then((module) => ({
    default: module.ProjectEmployees
  }))
);
const CreatePackage = lazy(() =>
  import("@surfaces/project-setup/pages/CreatePackage").then((module) => ({
    default: module.CreatePackage
  }))
);
const ViewProjectReport = lazy(() =>
  import("@surfaces/reports/pages/ViewProjectReport").then((module) => ({
    default: module.ViewProjectReport
  }))
);

export interface RouteInfo {
  index?: boolean;
  path: string;
  component: React.LazyExoticComponent<React.FC<any>> | React.FC<any>;
  layout: React.FC<any>;
}

const routesUnauthenticated: RouteInfo[] = [
  {
    component: Login,
    index: true,
    layout: BasicLayout,
    path: RouteHelper.login
  },
  {
    component: IS_TEST_ENV ? Register : Login,
    index: true,
    layout: BasicLayout,
    path: RouteHelper.register
  },
  {
    component: ForgotPassword,
    layout: BasicLayout,
    path: RouteHelper.forgotPassword
  },
  {
    component: ApiDocs,
    layout: BasicLayout,
    path: RouteHelper.apiDocs
  },
  {
    component: EmployeeSelfRegister,
    layout: NoMenuLayout,
    path: RouteHelper.employeeRegister(":token")
  },
  {
    component: McpDocs,
    layout: BasicLayout,
    path: RouteHelper.mcpDocs
  },
  {
    component: ClaudeDocs,
    layout: BasicLayout,
    path: RouteHelper.claudeDocs
  }
];

const routesAuthenticatedNoProfile: RouteInfo[] = [
  {
    component: CreateAccount,
    index: true,
    layout: NoMenuLayout,
    path: RouteHelper.createAccount
  }
];

const routesAuthenticated: RouteInfo[] = [
  {
    component: OrganisationDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.organisationDashboard
  },
  {
    component: CreateProject,
    layout: OrganisationLayout,
    path: RouteHelper.projectSetup(":projectId?")
  },
  {
    component: CreateEmployee,
    layout: OrganisationLayout,
    path: RouteHelper.employeeSetup(":employeeId?")
  },
  {
    component: Projects,
    index: true,
    layout: OrganisationLayout,
    path: RouteHelper.projects
  },
  {
    component: LastProjectRedirect,
    layout: OrganisationLayout,
    path: RouteHelper.lastProject
  },
  {
    component: CatchmentAdmin,
    layout: OrganisationLayout,
    path: RouteHelper.socialProAdmin
  },
  {
    component: EditAccount,
    layout: OrganisationLayout,
    path: RouteHelper.settings
  },
  {
    component: ApiDocs,
    layout: OrganisationLayout,
    path: RouteHelper.apiDocs
  },
  {
    component: McpDocs,
    layout: OrganisationLayout,
    path: RouteHelper.mcpDocs
  },
  {
    component: ClaudeDocs,
    layout: OrganisationLayout,
    path: RouteHelper.claudeDocs
  },
  {
    component: Employees,
    layout: OrganisationLayout,
    path: RouteHelper.employees
  },
  {
    component: ListSROI,
    layout: OrganisationLayout,
    path: RouteHelper.sroiBuilder
  },
  {
    component: SROIBuilder,
    layout: OrganisationLayout,
    path: RouteHelper.sroiBuilderCreate
  },
  {
    component: SROIReport,
    layout: OrganisationLayout,
    path: RouteHelper.sroiBuilderReport(":id")
  },
  {
    component: OrganisationDocumentsLevel1,
    layout: OrganisationLayout,
    path: RouteHelper.organisationDocumentsLevel1
  },
  {
    component: OrganisationDocumentsLevel2,
    layout: OrganisationLayout,
    path: RouteHelper.organisationDocumentsLevel2(":familyId")
  },
  // The Export Library pages were removed; old links land on the generator.
  {
    component: LegacyExportLibraryRedirect,
    layout: OrganisationLayout,
    path: "/organisation-export-library"
  },
  {
    component: OrganisationExportGenerator,
    layout: OrganisationLayout,
    path: RouteHelper.organisationExportGenerator
  },
  {
    component: FrameworkLibrary,
    layout: OrganisationLayout,
    path: RouteHelper.frameworkLibrary
  },
  {
    component: CreateFrameworkLibrary,
    layout: OrganisationLayout,
    path: RouteHelper.frameworkLibraryCreate
  },
  {
    component: CreateFrameworkLibrary,
    layout: OrganisationLayout,
    path: RouteHelper.frameworkLibraryEdit(":familyId")
  },
  {
    component: FrameworkVersions,
    layout: OrganisationLayout,
    // Last of the three, and dynamic: `create` and `:familyId/edit` are matched
    // first regardless of order, but keeping it here says so to a reader.
    path: RouteHelper.frameworkLibraryVersions(":familyId")
  },
  {
    component: OrganisationCompliance,
    layout: OrganisationLayout,
    path: RouteHelper.organisationCompliance(":frameworkId")
  },
  {
    component: OrgLabourHoursDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsLabourHours
  },
  {
    component: OrgSocialSpendDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsSocialSpend
  },
  {
    component: OrgEmissionsDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsEmissions
  },
  {
    component: OrgEnergyDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsEnergy
  },
  {
    component: OrgWaterDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsWater
  },
  {
    component: OrgWasteDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsWaste
  },
  {
    component: OrgMaterialsDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsMaterials
  },
  {
    component: OrgTransportDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsTransport
  },
  {
    component: OrgLocalContentDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsLocalContent
  },
  {
    component: OrgDocumentsDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsDocuments
  },
  {
    component: OrgFormsDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsForms
  },
  {
    component: OrgRegistersDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsRegisters
  },
  {
    component: OrgSurveysDashboard,
    layout: OrganisationLayout,
    path: RouteHelper.orgInsightsSurveys
  },
  {
    component: ForecastProjectLevel1,
    layout: ProjectDashboardLayout,
    path: RouteHelper.forecastProjectLevel1(":projectId")
  },
  {
    component: Forecast,
    layout: ProjectDashboardLayout,
    path: RouteHelper.forecast(":projectId", ":contractorPackageId")
  },
  {
    component: Dashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.contractorDashboard(":projectId", ":contractorPackageId")
  },
  {
    component: Dashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.contractorDashboardPackage(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: ContractorPackages,
    layout: ProjectDashboardLayout,
    path: RouteHelper.packages(":projectId", ":contractorPackageId")
  },
  {
    component: CreatePackage,
    layout: ProjectDashboardLayout,
    path: RouteHelper.packageSetup(
      ":projectId",
      ":contractorPackageId",
      ":packageNumber",
      ":subPackageId?"
    )
  },
  {
    component: PackageDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.contractorPackage(
      ":projectId",
      ":contractorPackageId",
      ":packageId"
    )
  },
  {
    component: ProjectReports,
    layout: ProjectDashboardLayout,
    path: RouteHelper.projectReports(":projectId", ":contractorPackageId")
  },
  {
    component: ProjectDetails,
    layout: ProjectDashboardLayout,
    path: RouteHelper.projectDetails(":projectId", ":contractorPackageId")
  },
  {
    component: PackageReports,
    layout: ProjectDashboardLayout,
    path: RouteHelper.contractorPackageReports(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: AnalyticsDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsLabourHoursLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: SocialSpendDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsSpendLevel1(":projectId", ":contractorPackageId")
  },
  {
    component: EmissionsDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsEmissionsLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: EnergyDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsEnergyLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: WaterDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsWaterLevel1(":projectId", ":contractorPackageId")
  },
  {
    component: WasteDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsWasteLevel1(":projectId", ":contractorPackageId")
  },
  {
    component: MaterialsDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsMaterialsLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: TransportDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsTransportLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: LocalContentDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsLocalContent(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: AnalyticsDocumentLevel1,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsDocumentsLevel1(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: ComplianceDashboard,
    layout: ProjectDashboardLayout,
    // The third segment is the framework's catalogue id (e.g. "VIC-4"), not the
    // per-project row uuid — see `analyticsCompliance` in utils/paths.ts.
    path: RouteHelper.analyticsCompliance(
      ":projectId",
      ":contractorPackageId",
      ":frameworkId"
    )
  },
  {
    component: AnalyticsDocumentLevel2,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsDocumentsLevel2(
      ":projectId",
      ":contractorPackageId",
      ":policyId"
    )
  },
  {
    component: AnalyticsDocumentLevel3,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsDocumentsLevel3(
      ":projectId",
      ":contractorPackageId",
      ":subPackageId",
      ":policyId"
    )
  },
  {
    component: GovAssureDocumentsDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsGovAssureDocuments(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: GovAssureFormsDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsGovAssureForms(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: GovAssureSurveysDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsGovAssureSurveys(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: GovAssureRegistersDashboard,
    layout: ProjectDashboardLayout,
    path: RouteHelper.analyticsGovAssureRegisters(
      ":projectId",
      ":contractorPackageId"
    )
  },
  {
    component: ReadOnlyReportSubmission,
    layout: ProjectDashboardLayout,
    path: RouteHelper.readOnlyMonthlyReport(
      ":projectId",
      ":contractorPackageId",
      ":packageId",
      ":reportId"
    )
  },
  {
    component: ReadOnlyReportSubmission,
    layout: ProjectDashboardLayout,
    path: RouteHelper.readOnlyPackageReport(
      ":projectId",
      ":contractorPackageId",
      ":packageId",
      ":reportId"
    )
  },
  {
    component: ReportSubmission,
    layout: ProjectDashboardLayout,
    path: RouteHelper.monthlyReportSubmission(
      ":projectId",
      ":contractorPackageId",
      ":reportId"
    )
  },
  {
    component: ReviewReportSection,
    layout: ProjectDashboardLayout,
    path: RouteHelper.reviewReportSection(
      ":projectId",
      ":contractorPackageId",
      ":packageId",
      ":reportId",
      ":section"
    )
  },
  {
    component: ReviewReportSection,
    layout: ProjectDashboardLayout,
    path: RouteHelper.reviewPackageReportSection(
      ":projectId",
      ":contractorPackageId",
      ":packageId",
      ":reportId",
      ":section"
    )
  },
  {
    component: ViewProjectReport,
    layout: ProjectDashboardLayout,
    path: RouteHelper.viewProjectReport(
      ":projectId",
      ":contractorPackageId",
      ":reportId"
    )
  },
  {
    component: CreateEmployee,
    layout: ProjectDashboardLayout,
    path: RouteHelper.projectEmployeeSetupSubmission(
      ":projectId",
      ":contractorPackageId",
      ":reportId",
      ":employeeId?"
    )
  },
  {
    component: CreateEmployee,
    layout: ProjectDashboardLayout,
    path: RouteHelper.projectEmployeeSetup(
      ":projectId",
      ":contractorPackageId",
      ":employeeId"
    )
  },
  {
    component: SmartDirectoryCompanies,
    layout: ProjectDashboardLayout,
    path: RouteHelper.smartDirectory(":projectId", ":contractorPackageId")
  },
  {
    component: ProjectEmployees,
    layout: ProjectDashboardLayout,
    path: RouteHelper.projectEmployees(":projectId", ":contractorPackageId")
  },
  {
    component: LegacyExportLibraryRedirect,
    layout: ProjectDashboardLayout,
    path: "/export-library/:projectId/:contractorPackageId"
  },
  {
    component: ExportGenerator,
    layout: ProjectDashboardLayout,
    path: RouteHelper.createExport(":projectId", ":contractorPackageId")
  }
];

// Agent prototype surfaces are restricted to SocialPro admins in non-prod only.
// Docs pages (mcpDocs, claudeDocs) are intentionally public — same as apiDocs.
const ADMIN_GATED_PATHS = new Set<string>([]);

export const getRoutes = (
  loggedIn: boolean,
  profile?: ProfileLineItem,
  isSocialProAdmin = false
) => {
  const showAgentRoutes = !IS_PROD_ENV && isSocialProAdmin;
  const gate = (routes: RouteInfo[]) =>
    showAgentRoutes
      ? routes
      : routes.filter((r) => !ADMIN_GATED_PATHS.has(r.path));

  if (loggedIn && (!profile || !profile.isSetup)) {
    return gate(routesAuthenticatedNoProfile);
  }
  if (loggedIn) {
    return gate(routesAuthenticated);
  }
  return gate(routesUnauthenticated);
};
